<template>
  <div class="flex flex-wrap items-start">
    <div class="flex items-center">
      <div
        :key="renderKey + '-icon'"
        class="mr-2 size-2.5 rounded-full"
        :class="{
          'bg-neutral-50': opensToday,
          'bg-[#5BD990]': open,
          'bg-[#ffcd3d]': closesSoon,
          'bg-[#f2594f]': closed,
        }"
      />
      <div class="mr-3 flex flex-col justify-center">
        <span
          :key="renderKey + '-color'"
          :class="{
            'text-neutral-50': opensToday,
            'text text-[#1DC463]': open,
            'text-[#edb000]': closesSoon,
            'text-[#ed564b]': closed,
          }"
        >
          {{ $t(currentStatusText) }}
        </span>
      </div>
    </div>
    <div v-if="!closed" class="flex flex-col gap-2">
      <span
        v-for="hours in dayOpeningHours"
        :key="hours"
        class="rounded-full bg-neutral-15 px-2"
      >
        {{ `${hours} ${$t('store_finder.hour')}` }}
      </span>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useIntervalFn } from '@vueuse/core'
import type { IStore } from '~/rpcMethods/stores'
import { STORE_STATUS } from '~/utils/store'
const $currentShop = useCurrentShop()

const renderKey = ref(0)

const props = defineProps({
  openingDetails: {
    type: Object as PropType<IStore>,
    default: () => {},
  },
})

const openingHours = computed(() =>
  getOpeningHoursToday(props.openingDetails, new Date()),
)

const dayOpeningHours = computed(() => {
  if (openingHours.value.length) {
    return openingHours.value.map(({ open, close }) => {
      return `${toLocaleDateTimeString(open, $currentShop.value, {
        hour: 'numeric',
        minute: 'numeric',
      })}-${toLocaleDateTimeString(close, $currentShop.value, {
        hour: 'numeric',
        minute: 'numeric',
      })}`
    })
  }
  return props.openingDetails.day_opening_hours || ''
})

const openingStatus = useState<STORE_STATUS>(
  `opening-status-${props.openingDetails.id}`,
  () =>
    getOpeningStatus(
      openingHours.value,
      Date.now(),
      props.openingDetails.opening,
    ),
)

onMounted(() => {
  openingStatus.value = getOpeningStatus(
    openingHours.value,
    Date.now(),
    props.openingDetails.opening,
  )
  renderKey.value += 1
  useIntervalFn(
    () => {
      const now = Date.now()
      openingStatus.value = getOpeningStatus(
        openingHours.value,
        now,
        props.openingDetails.opening,
      )
      renderKey.value += 1
    },
    1000 * 60,
    { immediateCallback: true },
  )
})

const opensToday = computed(
  () => openingStatus.value === STORE_STATUS.OPENS_TODAY,
)
const open = computed(() => openingStatus.value === STORE_STATUS.OPEN)
const closed = computed(() =>
  [STORE_STATUS.CLOSED, STORE_STATUS.CLOSED_TODAY].includes(
    openingStatus.value,
  ),
)
const closesSoon = computed(
  () => openingStatus.value === STORE_STATUS.CLOSING_SOON,
)

const currentStatusText = computed(() => {
  const openingStatusTextMap: Record<STORE_STATUS, string> = {
    CLOSED_TODAY: 'store_finder.closed_today',
    CLOSED: 'store_finder.closed',
    CLOSING_SOON: 'store_finder.closing_soon',
    OPEN: 'store_finder.open_today',
    OPENS_TODAY: 'store_finder.opens_today',
  }
  const emergencyText = getStoreEmergencyText(props.openingDetails)
  return emergencyText || openingStatusTextMap[openingStatus.value]
})
</script>
